import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import EditCard from '@components-clean/core/edit-card';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import styles from '@pages-clean/admin/study/manage-hubs/central-monitoring/style.module.scss';
import { useBoolean, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { ADD_STUDY_HUB_STUDY_FIELDS_CONFIG, GET_STUDY_HUB_STUDY_FIELDS_CONFIG } from '@queries/central-monitoring';
import {
    getStudyHubStudyFieldsConfig,
    getStudyHubStudyFieldsConfig_OpraQueries_getStudyHubStudyFieldsConfig_StudyHubStudyFieldsConfig,
    getStudyHubStudyFieldsConfigVariables,
    upsertStudyHubStudyFieldsConfig,
    upsertStudyHubStudyFieldsConfigVariables,
} from '@queries/generated/central-monitoring.generated';
import { studyHubStudyFieldsConfigFields } from '@queries/generated/fragments.generated';

const COMPONENT_NAME = 'studyHubStudyFields';

interface StudyFieldsConfigProps {
    studyRef: string;
    canEdit: boolean;
}

const StudyHubStudyFields = ({ studyRef, canEdit }: StudyFieldsConfigProps) => {
    const { confirmDiscardChanges, setComponentHasChanges, componentHasChanges } = useUnsavedChangesContext();

    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('admin', {
        keyPrefix: 'studyHubStudyFields',
    });
    const isEditMode = useBoolean(false);

    const [loadConfig, { data, loading: studyFieldsConfigLoading, refetch }] = useLazyQuery<
        getStudyHubStudyFieldsConfig,
        getStudyHubStudyFieldsConfigVariables
    >(GET_STUDY_HUB_STUDY_FIELDS_CONFIG);

    const [saveConfig, { loading: isMutatingStudyConfigData }] = useMutation<
        upsertStudyHubStudyFieldsConfig,
        upsertStudyHubStudyFieldsConfigVariables
    >(ADD_STUDY_HUB_STUDY_FIELDS_CONFIG);

    useEffect(() => {
        loadConfig({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
    }, [studyRef]);

    const defaultValues = {
        numGreenSites: data?.getStudyHubStudyFieldsConfig?.numGreenSites,
        numAmberSites: data?.getStudyHubStudyFieldsConfig?.numAmberSites,
        numRedSites: data?.getStudyHubStudyFieldsConfig?.numRedSites,
        studySiteMean: data?.getStudyHubStudyFieldsConfig?.studySiteMean,
        countrySiteMean: data?.getStudyHubStudyFieldsConfig?.countrySiteMean,
        studyRate: data?.getStudyHubStudyFieldsConfig?.studyRate,
        siteRate: data?.getStudyHubStudyFieldsConfig?.siteRate,
    };

    const {
        control,
        reset,
        handleSubmit,
        formState: { isDirty },
    } = useForm<getStudyHubStudyFieldsConfig_OpraQueries_getStudyHubStudyFieldsConfig_StudyHubStudyFieldsConfig>({
        defaultValues,
    });

    useEffect(() => {
        const numGreenSites = data?.getStudyHubStudyFieldsConfig?.numGreenSites;
        const numAmberSites = data?.getStudyHubStudyFieldsConfig?.numAmberSites;
        const numRedSites = data?.getStudyHubStudyFieldsConfig?.numRedSites;
        const studySiteMean = data?.getStudyHubStudyFieldsConfig?.studySiteMean;
        const countrySiteMean = data?.getStudyHubStudyFieldsConfig?.countrySiteMean;
        const studyRate = data?.getStudyHubStudyFieldsConfig?.studyRate;
        const siteRate = data?.getStudyHubStudyFieldsConfig?.siteRate;
        reset(
            {
                numGreenSites,
                numAmberSites,
                numRedSites,
                studySiteMean,
                countrySiteMean,
                studyRate,
                siteRate,
            },
            { keepDirty: false },
        );
    }, [data]);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
    }, [isDirty]);

    useEffect(() => {
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, []);

    useEffect(() => {
        if (!isEditMode.value) {
            setComponentHasChanges(COMPONENT_NAME, false);
        }
    }, [isEditMode.value]);

    const onSubmit = handleSubmit(async (saveData: studyHubStudyFieldsConfigFields) => {
        if (isDirty) {
            saveConfig({
                variables: {
                    studyId: studyRef,
                    studyHubStudyFieldsConfig: {
                        numGreenSites: saveData?.numGreenSites,
                        numAmberSites: saveData?.numAmberSites,
                        numRedSites: saveData?.numRedSites,
                        studySiteMean: saveData?.studySiteMean,
                        countrySiteMean: saveData?.countrySiteMean,
                        studyRate: saveData?.studyRate,
                        siteRate: saveData?.siteRate,
                    },
                },
            })
                .then(async () => {
                    isEditMode.setFalse();
                    ShowSuccess(tRoot('actionsSuccessfulToast'));
                    await refetch();
                })
                .catch(() => {
                    ShowError(t('errorMessage'));
                });
        } else {
            isEditMode.setFalse();
        }
    });

    const isLoading = !data || studyFieldsConfigLoading || isMutatingStudyConfigData;
    return (
        <EditCard
            title={t('studyFields')}
            onEdit={() => isEditMode.setTrue()}
            onCancel={async () => {
                if (componentHasChanges(COMPONENT_NAME)) {
                    const ok = await confirmDiscardChanges();
                    if (!ok) {
                        return;
                    }
                    setComponentHasChanges(COMPONENT_NAME, false);
                }
                reset(defaultValues, { keepDirty: false });
                isEditMode.setFalse();
            }}
            onSave={onSubmit}
            isEditMode={isEditMode.value}
            disabled={!canEdit || isLoading}
            data-testid="panels-config"
        >
            {isLoading && <Loading />}
            {!isLoading && (
                <div className={styles.checkboxHolder}>
                    <div className={styles.checkboxItem}>
                        <Controller
                            control={control}
                            name="numGreenSites"
                            render={({ field: { onChange, value } }) => (
                                <CoreCheckbox
                                    value={value}
                                    onChange={onChange}
                                    disabled={!canEdit || isLoading || !isEditMode.value}
                                    label={t('fields.numGreenSites')}
                                    data-testid="checkbox-numGreenSites"
                                />
                            )}
                        />
                    </div>
                    <div className={styles.checkboxItem}>
                        <Controller
                            control={control}
                            name="numAmberSites"
                            render={({ field: { onChange, value } }) => (
                                <CoreCheckbox
                                    value={value}
                                    onChange={onChange}
                                    disabled={!canEdit || isLoading || !isEditMode.value}
                                    label={t('fields.numAmberSites')}
                                    data-testid="checkbox-numAmberSites"
                                />
                            )}
                        />
                    </div>
                    <div className={styles.checkboxItem}>
                        <Controller
                            control={control}
                            name="numRedSites"
                            render={({ field: { onChange, value } }) => (
                                <CoreCheckbox
                                    value={value}
                                    onChange={onChange}
                                    disabled={!canEdit || isLoading || !isEditMode.value}
                                    label={t('fields.numRedSites')}
                                    data-testid="checkbox-numRedSites"
                                />
                            )}
                        />
                    </div>
                    <div className={styles.checkboxItem}>
                        <Controller
                            control={control}
                            name="studySiteMean"
                            render={({ field: { onChange, value } }) => (
                                <CoreCheckbox
                                    value={value}
                                    onChange={onChange}
                                    disabled={!canEdit || isLoading || !isEditMode.value}
                                    label={t('fields.studySiteMean')}
                                    data-testid="checkbox-studySiteMean"
                                />
                            )}
                        />
                    </div>
                    <div className={styles.checkboxItem}>
                        <Controller
                            control={control}
                            name="countrySiteMean"
                            render={({ field: { onChange, value } }) => (
                                <CoreCheckbox
                                    value={value}
                                    onChange={onChange}
                                    disabled={!canEdit || isLoading || !isEditMode.value}
                                    label={t('fields.countrySiteMean')}
                                    data-testid="checkbox-studySiteMean"
                                />
                            )}
                        />
                    </div>
                    <div className={styles.checkboxItem}>
                        <Controller
                            control={control}
                            name="studyRate"
                            render={({ field: { onChange, value } }) => (
                                <CoreCheckbox
                                    value={value}
                                    onChange={onChange}
                                    disabled={!canEdit || isLoading || !isEditMode.value}
                                    label={t('fields.studyRate')}
                                    data-testid="checkbox-studyRate"
                                />
                            )}
                        />
                    </div>
                    <div className={styles.checkboxItem}>
                        <Controller
                            control={control}
                            name="siteRate"
                            render={({ field: { onChange, value } }) => (
                                <CoreCheckbox
                                    value={value}
                                    onChange={onChange}
                                    disabled={!canEdit || isLoading || !isEditMode.value}
                                    label={t('fields.siteRate')}
                                    data-testid="checkbox-siteRate"
                                />
                            )}
                        />
                    </div>
                </div>
            )}
        </EditCard>
    );
};

export default StudyHubStudyFields;
